import type { MaterialGroup, PartItem } from '@cutr/constants/cutlist';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePartsErrorStore } from '@/api/errors';
import { getMaterial } from '@/api/materials';
import { useMaterialGroupState } from '@/api/materialsGroup';
import {
  type MixedNestingState,
  useMixedNestingStore,
} from '@/api/mixedNesting';
import { partsSelector, useCutlistState } from '@/api/store';

import type { NestingJobError, NestingJobGroupStatus } from './cutlists';

export type NestingGroupFailures = Record<string, string>;

export const nestingErrorMessageKey = (code: string): string => {
  switch (code) {
    case 'PART_DOES_NOT_FIT_ANY_STOCK':
      return 'cutlist-summary.nesting.partsDoNotFitAnyStock';
    case 'OUT_OF_STOCK':
      return 'cutlist-summary.nesting.outOfStock';
    case 'NO_LAYOUT_PRODUCED':
      return 'cutlist-summary.nesting.noLayoutProduced';
    case 'INTERNAL_ERROR':
      return 'cutlist-summary.nesting.failed';
    case 'INVALID_REQUEST':
      return 'cutlist-summary.nesting.terminal';
    default:
      return 'cutlist-summary.nesting.terminal';
  }
};

export const largestStockSize = (error?: NestingJobError): string | null => {
  const stock = error?.largestStock;
  return stock ? [stock.height, stock.width].join('x') : null;
};

const handlePart = ({ part, text }: { part: PartItem; text: string }): void => {
  usePartsErrorStore.getState().addPartError(part.id, 'nesting', text);
};

const applyGroupPartErrors = ({
  failed,
  group,
  allParts,
  t,
}: {
  failed: NestingJobGroupStatus;
  group: MaterialGroup | undefined;
  allParts: PartItem[];
  t: TFunction;
}): void => {
  if (failed.error?.code !== 'PART_DOES_NOT_FIT_ANY_STOCK') return;

  const core = group ? getMaterial(group.core1) : undefined;
  const sheetSize =
    largestStockSize(failed.error) ??
    (core ? [core.lengthMM, core.widthMM].join('x') : '');

  for (const { clientPartId } of failed.error.parts ?? []) {
    if (!clientPartId) continue;
    const part = allParts.find((p) => p.shortId === clientPartId);
    if (!part) continue;
    const text = t('cutlist-summary.nesting.partDoesNotFitAnyStock', {
      name: part.label,
      partSize: [part.lengthMM, part.widthMM].join('x'),
      sheetSize,
    });

    handlePart({ part, text });
  }
};

export const applyNestingPartErrors = (
  failedGroups: NestingJobGroupStatus[],
  t: TFunction
): void => {
  usePartsErrorStore.getState().resetPartsNestingError();

  const allParts = partsSelector(useCutlistState.getState());
  const allGroups = useMaterialGroupState.getState().groups;

  for (const failed of failedGroups) {
    const group = allGroups.find((g) => g.id === failed.groupId);
    applyGroupPartErrors({ failed, group, allParts, t });
  }
};

export const nestingGroupFailures = (
  failedGroups: NestingJobGroupStatus[],
  t: TFunction
): NestingGroupFailures =>
  Object.fromEntries(
    failedGroups.map((failed) => [
      failed.groupId,
      t(nestingErrorMessageKey(failed.error?.code ?? '')),
    ])
  );

export const nestingFailureMessage = ({
  nestingState,
  groupFailures,
  activeGroupId,
  t,
}: {
  nestingState: MixedNestingState;
  groupFailures: NestingGroupFailures | null;
  activeGroupId: string | undefined;
  t: TFunction;
}): string | null => {
  if (nestingState !== 'failed') return null;
  if (!groupFailures) return t('cutlist-summary.nesting.failed');
  return (
    (activeGroupId && groupFailures[activeGroupId]) ||
    t('cutlist-summary.nesting.failedInOtherGroup')
  );
};

export const useNestingFailureMessage = (
  activeGroupId: string | undefined
): string | null => {
  const { t } = useTranslation();
  const nestingState = useMixedNestingStore((state) => state.nestingState);
  const failedGroups = useMixedNestingStore((state) => state.failedGroups);

  React.useEffect(() => {
    if (failedGroups) applyNestingPartErrors(failedGroups, t);
  }, [failedGroups, t]);

  return nestingFailureMessage({
    nestingState,
    groupFailures: failedGroups && nestingGroupFailures(failedGroups, t),
    activeGroupId,
    t,
  });
};
